import { Event } from "../../../../Global/Base/Events";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { IPopupMenu } from "../IPopupMenu";
import { PopupMenu } from "../PopupMenu";
import { IChartProfileInfo } from "../XGControls/Forms/IFormProfileInfo";
import { ChartFrame } from "../XGControls/Frames/ChartFrame";
import { IContentBuilder } from "./IContentBuilder";

export class ChartNameBuilder implements IContentBuilder {
    public changed: Event<ChartNameBuilder, string> = new Event<ChartNameBuilder, string>();

    private _cssClass: string = "chart-name";
    private _frame: ChartFrame;
    private _errorPopup: IPopupMenu;
    private _htmlElement: HTMLElement;
    private _textElement: HTMLElement;
    private _textPromise: Promise<any>;

    constructor(frame: ChartFrame) {
        this._frame = frame;
    }

    private _createElement(): HTMLElement {
        this._htmlElement = createElement({ classes: ["ToolbarButton"] });
        this._textElement = createElement({
            type: "span",
            classes: this._cssClass,
        });
        this._textElement.setAttribute("contenteditable", "true");

        this._textElement.addEventListener("focus", () => {
            this._textElement.classList.add(this._cssClass + "__edited");
        });

        this._textElement.addEventListener("blur", () => {
            this._htmlElement.setAttribute("title", this._textElement.textContent);
            this._textElement.scrollLeft = 0;
            this._textElement.classList.remove(this._cssClass + "__edited");

            if (this.changed)
                this.changed.trigger(this, this._textElement.textContent);
        });

        this._textElement.addEventListener("keydown", (e) => {
            if (e.key == "Enter")
                e.preventDefault();
        });

        this._textElement.addEventListener("keyup", () => {
            if (this._textElement.textContent == "") {
                this._textElement.classList.add(this._cssClass + "__invalid");

                if (!this._errorPopup) {
                    const errorElement = createElement({ classes: "errDiv" });
                    Localization.getLocalizationItem("GUI-55627").then((locItem) => {
                        errorElement.textContent = locItem.primaryText;
                        errorElement.style.display = "block";
                        this._errorPopup = new PopupMenu(errorElement, false, null, null, null, null, null);
                        this._errorPopup.arangePopupToEditableControl(errorElement, this._textElement, true);
                    });
                }
            } else if (this._textElement.classList.contains(this._cssClass + "__invalid")) {
                this._textElement.classList.remove(this._cssClass + "__invalid");
                this._errorPopup.setVisibility(false);
            }
        });

        this._textElement.addEventListener("mouseenter", () => {
            if (this._textElement.classList.contains(this._cssClass + "__invalid") && this._errorPopup)
                this._errorPopup.setVisibility(true);
        });

        this._textElement.addEventListener("mouseleave", () => {
            if (this._textElement.classList.contains(this._cssClass + "__invalid") && this._errorPopup)
                this._errorPopup.setVisibility(false);
        });

        this._htmlElement.appendChild(this._textElement);
        this._registerEventAndLoadData();

        return this._htmlElement;
    }

    private _registerEventAndLoadData(): void {
        const f_loadData = async (profile: IChartProfileInfo) => {
            if (this._textPromise)
                await this._textPromise;

            if (profile.chartName) {
                this._textElement.textContent = profile.chartName;
                this._htmlElement.setAttribute("title", profile.chartName);
            } else {
                this._textPromise = Localization.getTextPromise(this._frame.locKey).then(name => {
                    this._textElement.textContent = name;
                    this._htmlElement.setAttribute("title", name);
                    profile.chartName = name;
                    this._textPromise = null;
                });
            }
        };

        this._frame.frameProfileChanged.add(() => f_loadData(this._frame.profileInfo as IChartProfileInfo));
        this._frame.selectedProfileChanged.add((_frame, profile: IChartProfileInfo) => f_loadData(profile));
        this._frame.chartNameChanged.add((_frame, profile: IChartProfileInfo) => f_loadData(profile));

        f_loadData(this._frame.profileInfo as IChartProfileInfo);
    }

    public build(): Promise<HTMLElement> {
        return Promise.resolve(this._createElement());
    }
}
